import {
    AgentStub,
    CrewTenure,
    ensureCrewByTruck,
    ensureStampsForTruck,
    ensureTransferById,
    ensureTransfersForTruck,
    ensureTriplegById,
    ensureTriplegsForTruck,
    ensureTruckById,
    KingBasura,
    SortOrder,
    Transfer,
    Tripleg,
    TruckStamp,
    TruckStampFlavor,
    TruckStub,
    useCrewByTruck,
    useFullTransfersForTruck,
    useFullTriplegsForTruck,
    useLastTriplegForTruck,
    useStampsForTruck,
    useTruckById
} from "../rpc/webrpc-client.gen.ts";
import {
    BaseTimelineScene,
    cmpActorScenesNewestFirst,
    cmpScenesNewestFirst,
    cmpTimeCoords
} from "../timeline/timeline.tsx";
import {VenueCapsule} from "../venue/capsule.tsx";
import {QueryClient, queryOptions, skipToken, useQueryClient} from "@tanstack/react-query";
import {Flexidate, spawnTstzSpan, TstzSpan} from "../tstzspan.ts";
import {StampSceneView, StampTimelineTileContent} from "../stamp/stamps.tsx";
import {Box, Sheet, Stack, useTheme} from "@mui/joy";
import {useEffect, useMemo, useState} from "react";
import {AgentCapsule} from "../agent/capsule.tsx";
import {faCarBurst, faSteeringWheel} from "@fortawesome/pro-regular-svg-icons";
import {BundleCapsule} from "../bundle/capsule.tsx";
import {cleanse, cmpById, Comparator} from "../util/arutil.tsx";
import {RouterJoyLink} from "../link.tsx";
import {z} from "zod/v4";
import {synthTriplegScenes, TriplegScene, TriplegSceneTileContent, useTriplegSceneColor} from "../transit/tripleg.tsx";
import {
    synthTransferScenes,
    TransferScene,
    TransferSceneTileContent,
    useTransferSceneColor
} from "../transit/transfer.tsx";
import {SceneTileContent} from "../dispatch/DispatchTile.tsx";
import {DateBrowser} from "../components/Dates.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {useTranslation} from "react-i18next";
import {Tile} from "../components/Tile.tsx";
import {TruckCapsule} from "./capsule.tsx";
import {HCBox} from "../components/HCBox.tsx";
import {DateArg} from "date-fns";
import {StampFlavorIconMap} from "../stamp/stampFlavorIconMap.tsx";
import {InfallibleUseQueryResult} from "../util/useInfallibleQuery.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {SceneViewProps} from "../timeline/sceneViewProps.tsx";
import {DiarySceneView, DiaryView} from "../timeline/diary.tsx";
import {TieredOrActive} from "../dispatch/tieredOrActive.tsx";
import {TimelineTimePoint} from "../timeline/timelineTimeCoordinate.tsx";
import {formatTileTimelineCoordinate} from "../timeline/formatTileTimelineCoordinate.ts";
import {useMothership} from "../tq/tqx.tsx";

export interface TruckTimeslice {
    truckId: string;
    span: TstzSpan;
}

const TruckSceneBase = z.object({
    ...BaseTimelineScene.shape,
    subjectFlavor: z.literal("truck"),
    subject: TruckStub,
});

export const TruckPOVTriplegScene = z.object({
    ...TruckSceneBase.shape,
    sceneFlavor: z.literal("tripleg"),
    triplegScene: TriplegScene,
});
export type TruckPOVTriplegScene = z.infer<typeof TruckPOVTriplegScene>;

function rebadgeTriplegScene(from: TriplegScene): TruckPOVTriplegScene {
    const rv: TruckPOVTriplegScene = {
        subjectFlavor: "truck",
        subject: from.subject.truck,
        sceneFlavor: "tripleg",
        triplegScene: from,
        when: from.when,
        where: from.where,
    };
    return rv;
}

export const TruckPOVTransferScene = z.object({
    ...TruckSceneBase.shape,
    sceneFlavor: z.literal("transfer"),
    transferScene: TransferScene,
});
export type TruckPOVTransferScene = z.infer<typeof TruckPOVTransferScene>;

function rebadgeTransferScene(from: TransferScene): TruckPOVTransferScene {
    const rv: TruckPOVTransferScene = {
        subjectFlavor: "truck",
        subject: from.subject.truck,
        sceneFlavor: "transfer",
        transferScene: from,
        when: from.when,
        where: from.where,
    };
    return rv;
}

export const TruckStampScene = TruckSceneBase.extend({
    sceneFlavor: z.literal("stamp"),
    stamp: TruckStamp,
});
export type TruckStampScene = z.infer<typeof TruckStampScene>;

export const TruckCrewScene = TruckSceneBase.extend({
    sceneFlavor: z.literal("crew"),
    driver: AgentStub,
    direction: z.literal(["mount", "dismount"]),
    when: TimelineTimePoint,
});
export type TruckCrewScene = z.infer<typeof TruckCrewScene>;

export const TruckScene
    = z.discriminatedUnion("sceneFlavor",
    [TruckPOVTriplegScene, TruckPOVTransferScene,
        TruckStampScene, TruckCrewScene]);
export type TruckScene = z.infer<typeof TruckScene>;

export function synthTruckStampScenes({truck, stamps}: { truck: TruckStub, stamps: TruckStamp[] }): TruckStampScene[] {
    const rv = stamps.map(stamp => ({
        subjectFlavor: "truck",
        subject: truck,
        sceneFlavor: "stamp",
        stamp,
        when: {at: stamp.posted}
    } as const));

    return rv;
}

export const iconsForTruckStampFlavor: StampFlavorIconMap<"truck"> = {
    [TruckStampFlavor.enum.ACCIDENT]: {icon: faCarBurst},
} as const;

// function TruckStampTimelineCellContent({scene}: { scene: TruckStampScene }) {
//     const {t} = useTranslation();
//     const stampFlavor = scene.stamp.flavor;
//     if (stampFlavor) {
//         const icon = iconsForTruckStampFlavor[stampFlavor];
//         return <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//             {icon ? <FontAwesomeIcon {...icon} size="2x"/> : null}
//             <Box>{t(`stampFlavorTileLabels.truck.${stampFlavor}`)}</Box>
//         </Stack>;
//     } else {
//         return <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//             <FontAwesomeIcon icon={faComment} size="2x"/>
//         </Stack>;
//     }
// }

export function synthCrewTenureScenes({truck, crewTenures: tenures}: {
    truck: TruckStub,
    crewTenures: CrewTenure[]
}): TruckCrewScene[] {
    const rv: TruckCrewScene[] = [];

    for (const tenure of tenures) {
        const validSpan = spawnTstzSpan(tenure.validSpan);
        if (validSpan.lower) {
            rv.push({
                subjectFlavor: "truck",
                subject: truck,
                sceneFlavor: "crew",
                when: {at: validSpan.lower},
                direction: "mount",
                driver: tenure.crew,
            });
        }
        if (validSpan.upper) {
            rv.push({
                subjectFlavor: "truck",
                subject: truck,
                sceneFlavor: "crew",
                when: {at: validSpan.upper},
                direction: "dismount",
                driver: tenure.crew,
            });
        }
    }

    return rv;
}

// const CrewTenureSceneDecoratorArgsSchema = makeSceneDecoratorArgsSchema(TruckCrewSceneSchema);
// type CrewTenureSceneDecoratorArgs = z.infer<typeof CrewTenureSceneDecoratorArgsSchema>;

// function useCrewTenureTimelineSceneLabel(scene: TruckCrewScene) {
//     const {t} = useTranslation();
//     return t(scene.direction === "mount" ? "truck.driverMounted" : "truck.driverUnmounted");
// }
//
// function CrewTenureTimelineCellContent({scene}: { scene: TruckCrewScene }): TimelineSceneViewProps {
//     const label = useCrewTenureTimelineSceneLabel(scene);
//     const driver = {...scene.driver, icon: {icon: faSteeringWheel}};
//     return <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//         <EntityCapsule entity={driver}/>
//         <Box>{label}</Box>
//     </Stack>;
// }
//
// export const CrewTenureTileSceneContentPropsSchema = makeTimelineTileContentPropsSchema(TruckCrewSceneSchema);
// export type CrewTenureTileSceneContentProps = z.infer<typeof CrewTenureTileSceneContentPropsSchema>;
//
// export function CrewTenureTileSceneContent(props: CrewTenureTileSceneContentProps) {
//     const scene = props.scene;
//     const label = useCrewTenureTimelineSceneLabel(scene);
//     const color = props.color;
//     return <SceneTileContent color={color} icon={{icon: faSteeringWheel}}
//                              what={<Stack direction="row">
//                                  <AgentCapsule agent={scene.driver}/>
//                                  <Box>{label}</Box>
//                              </Stack>} when={scene.when.at}/>
// }

export const TruckTimelineFood = z.object({
    truck: TruckStub,
    triplegs: Tripleg.array(),
    triplegsAsof: z.optional(Flexidate),
    stamps: TruckStamp.array(),
    stampsAsof: z.optional(Flexidate),
    transfers: Transfer.array(),
    transfersAsof: z.optional(Flexidate),
    crewTenures: CrewTenure.array(),
    crewTenuresAsof: z.optional(Flexidate),
});
export type TruckTimelineFood = z.infer<typeof TruckTimelineFood>;

// newest first

export function synthTruckScenes(food: TruckTimelineFood): TruckScene[] {
    const unsorted: TruckScene[] = [
        ...synthTriplegScenes(food).map(rebadgeTriplegScene),
        ...synthTransferScenes(food).map(rebadgeTransferScene),
        ...synthTruckStampScenes(food),
        ...synthCrewTenureScenes(food),
    ];

    const sorted = unsorted
        .toSorted((a, b) =>
            cmpTimeCoords(b.when, a.when));
    return sorted;
}

function TruckPOVTriplegSceneView({scene, ...rest}: SceneViewProps<TruckPOVTriplegScene>) {
    const {t} = useTranslation();

    return <DiarySceneView scene={scene} {...rest}>
        {scene.where?.venue ? <VenueCapsule venue={scene.where.venue}/> : null}
        <Box>{t(`driver.${scene.triplegScene.sceneFlavor}`)}</Box>
    </DiarySceneView>
}

function TruckTriplegSceneTileContent({scene, ...rest}: SceneViewProps<TruckPOVTriplegScene>) {
    return <TriplegSceneTileContent scene={scene.triplegScene} {...rest}/>
}

function TruckPOVTransferSceneView({scene, ...rest}: SceneViewProps<TruckPOVTransferScene>) {
    const {t} = useTranslation();
    const transferScene = scene.transferScene;
    const bundle = transferScene.subject.bundle;
    const i18k = transferScene.direction === "PICKUP"
        ? (transferScene.sceneFlavor === "start" ? "driver.startOnloading" : "driver.onloaded")
        : (transferScene.sceneFlavor === "start" ? "driver.offloading" : "driver.offloaded");
    const venue = transferScene.subject[
        scene.transferScene.direction === "PICKUP" ? "onloadVenue" : "offloadVenue"];

    return <DiarySceneView scene={scene} {...rest}>
        <Stack direction="row">
            {venue ? <VenueCapsule venue={venue}/> : null}
            <RouterJoyLink to="/bundle/$id/cargo" params={{id: bundle.id}}>
                <BundleCapsule bundle={bundle}/>
            </RouterJoyLink>
            <Box>{t(i18k)}</Box>
        </Stack>
    </DiarySceneView>
}

export function TruckTransferSceneTileContent({scene, color: inColor, ...rest}: SceneViewProps<TruckPOVTransferScene>) {
    const transferScene = scene.transferScene;
    const bundle = transferScene.subject.bundle;
    const intrinsicColor = useTransferSceneColor(transferScene);
    const color = inColor ?? intrinsicColor;

    return <TransferSceneTileContent scene={transferScene} color={color} {...rest}>
        <RouterJoyLink to="/bundle/$id/cargo" params={{id: bundle.id}} color={color}>
            <BundleCapsule bundle={bundle}/>
        </RouterJoyLink>
    </TransferSceneTileContent>;
}

function useCrewTenureSceneLabel(scene?: TruckCrewScene) {
    const {t} = useTranslation();
    if (!scene) {
        return undefined;
    }
    const i18k = scene.direction === "mount" ? "truck.driverMounted" : "truck.driverUnmounted";
    return t(i18k);
}

function CrewSceneView({scene, ...rest}: SceneViewProps<TruckCrewScene>) {
    const label = useCrewTenureSceneLabel(scene);

    return <DiarySceneView scene={scene} {...rest}>
        <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
            <AgentCapsule agent={{...scene.driver, icon: {icon: faSteeringWheel}}}/>
            <Box>{label}</Box>
        </Stack>
    </DiarySceneView>
}

function CrewSceneTileContent({scene, ...rest}: SceneViewProps<TruckCrewScene>) {
    const label = useCrewTenureSceneLabel(scene);

    return <SceneTileContent
        what={<Stack direction="row">
            <AgentCapsule agent={scene.driver}/>
            <Box>{label}</Box>
        </Stack>}
        when={scene.when}
        icon={{icon: faSteeringWheel}}
        {...rest}
    />
}

export function TruckSceneView({scene, ...rest}: SceneViewProps<TruckScene>) {
    switch (scene.sceneFlavor) {
        case "tripleg":
            return TruckPOVTriplegSceneView({scene, ...rest});
        case "transfer":
            return TruckPOVTransferSceneView({scene, ...rest});
        case "crew":
            return CrewSceneView({scene, ...rest});
        case "stamp":
            return StampSceneView<"truck">({scene, iconMap: iconsForTruckStampFlavor, ...rest});
    }
}

export function TruckSceneTileContent<SceneT extends TruckScene>({scene, ...rest}: SceneViewProps<SceneT>) {
    switch (scene.sceneFlavor) {
        case "tripleg":
            return TruckTriplegSceneTileContent({scene, ...rest});
        case "transfer":
            return TransferSceneTileContent({scene: scene.transferScene, ...rest});
        case "crew":
            return CrewSceneTileContent({scene, ...rest});
        case "stamp":
            return StampTimelineTileContent<"truck">({scene, iconMap: iconsForTruckStampFlavor, ...rest});
    }
}

export function TruckSceneTile({scene, color: inColor, variant, ...rest}: SceneViewProps<TruckScene>) {
    const truck = scene.subject;
    const lastTriplegTqrv = useLastTriplegForTruck({truckId: truck.id});
    const lastTripleg = lastTriplegTqrv.data;
    const theme = useTheme();
    const {t} = useTranslation();
    const color = inColor;
    return <Tile variant="outlined" color={color}
                 Subject={(props) => <RouterJoyLink
                     to="/truck/$id/cargo" params={{id: truck.id}}
                     color={color} underline="hover">
                     <TruckCapsule truck={truck} {...props}/>
                 </RouterJoyLink>}>
        {/* make sure destination shows even on the road */
            !!lastTripleg?.departed && !lastTripleg.arrived
                ? <Stack direction="row" sx={{fontSize: theme.fontSize.md, alignItems: "baseline"}}>
                    <VenueCapsule venue={lastTripleg.destination}/>
                    <Box sx={{flex: 0, ml: 0.6}}>{t("driver.destination")}</Box>
                </Stack>
                : null}

        <TruckSceneTileContent scene={scene} color={color} {...rest}/>

        {/*{crewCapsules}*/}

        <Sheet color={color} variant={variant}>
            <HCBox>
                {t("dispatch.asof", {
                    when: formatTileTimelineCoordinate(scene.asof ? {at: scene.asof} : scene.when),
                })}
            </HCBox>
        </Sheet>
    </Tile>;
}

export const TruckActor = z.object({
    truck: TruckStub,
    lastScene: z.nullable(TruckScene),
});
export type TruckActor = z.infer<typeof TruckActor>;

export interface TruckActors {
    truckActors: TruckActor[];
}

export interface TruckStubs {
    truckStubs: TruckStub[];
}

// function useTruckStampTimelineScenes(slice: TruckTimeslice): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const {truckId, span} = slice;
//     const mama = useMothership();
//     const {data: stampsData, isSuccess: isStampsSuccess}
//         = useRefreshableQuery(truckStampsDuringTqxo(mama, {truckId, span}));
//
//     if (!isStampsSuccess) {
//         return MADA;
//     }
//     const stamps = stampsData.stamps;
//
//     const rv: TimelineSceneViewProps[] = [];
//     for (const stamp of stamps) {
//         switch (stamp.flavor) {
//             default:
//                 rv.push({
//                     when: {at: stamp.posted},
//                     what: <StampTimelineEntry stamp={stamp} color="neutral" variant="soft"/>,
//                     color: "neutral",
//                 });
//         }
//     }
//
//     return {data: rv, isSuccess: true};
// }
//
// function useTruckCrewTenureTimelineScenes({
//                                               truckId,
//                                               span
//                                           }: TruckTimeslice): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const mama = useMothership();
//     const {data: tenuresData, isSuccess: isTenuresSuccess}
//         = useRefreshableQuery(crewTenuresByTruckDuringTqo(mama, {truckId, during: span.toString()}));
//     const {t} = useTranslation();
//
//     if (!isTenuresSuccess) {
//         return MADA;
//     }
//     const tenures = tenuresData.tenures;
//
//     const rv: TimelineSceneViewProps[] = tenures.reduce<TimelineSceneViewProps[]>((acc, tenure) => {
//         const tenureSpan = new TstzSpan(tenure.validSpan);
//         const driverCapsule =
//             <AgentCapsule agent={{...tenure.crew, icon: {icon: faSteeringWheel}}}/>;
//         if (span.contains(tenureSpan.lower)) {
//             acc.push({
//                 when: {at: tenureSpan.lower!},
//                 what: <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//                     {driverCapsule}
//                     <Box>{t("truck.driverMounted")}</Box>
//                 </Stack>,
//                 color: "neutral",
//             });
//         }
//         if (span.contains(tenureSpan.upper)) {
//             acc.push({
//                 when: {at: tenureSpan.upper!},
//                 what: <Stack direction="row" sx={{alignItems: "center"}} spacing={0.6}>
//                     {driverCapsule}
//                     <Box>{t("truck.driverUnmounted")}</Box>
//                 </Stack>,
//                 color: "neutral",
//             });
//         }
//         return acc;
//     }, []);
//
//     return {data: rv, isSuccess: true};
// }
//
// function makeTriplegTimelineSceneProps(
//     {t, tripleg, cutspan}: {
//         t: TFunction,
//         tripleg: Tripleg,
//         cutspan?: TstzSpan
//     }): TimelineSceneViewProps[] {
//     const rv: TimelineSceneViewProps[] = [];
//
//     if (!cutspan || cutspan.contains(tripleg.departed)) {
//         const originVenue: CapsulableEntity = tripleg.origin ?? {displayName: t("unknownVenue")};
//         const color = "neutral";
//         const variant = "soft";
//         rv.push({
//             when: {at: tripleg.departed},
//             what: <TimelineCell>
//                 <VenueCapsule venue={originVenue}/>
//                 <Box>{t("driver.departed")}</Box>
//             </TimelineCell>,
//             color: color,
//             variant: variant,
//         });
//     }
//
//     if (tripleg.arrived && (!cutspan || cutspan.contains(tripleg.arrived))) {
//         const color = "neutral";
//         const variant = "soft";
//         if (tripleg.admitted && (!cutspan || cutspan.contains(tripleg.admitted))) {
//             if (differenceInMinutes(tripleg.arrived, tripleg.admitted) > 0) {
//                 rv.push({
//                     when: {at: tripleg.arrived},
//                     what: <TimelineCell>
//                         <VenueCapsule venue={tripleg.destination}/>
//                         <Box>{t("driver.arrived")}</Box>
//                     </TimelineCell>,
//                     color: color,
//                     variant: variant,
//                 }, {
//                     when: {at: tripleg.admitted},
//                     what: <TimelineCell>
//                         <VenueCapsule venue={tripleg.destination}/>
//                         <Box>{t("driver.admitted")}</Box>
//                     </TimelineCell>,
//                     color: color,
//                     variant: variant,
//                 });
//             } else {
//                 rv.push({
//                     when: {at: tripleg.admitted},
//                     what: <TimelineCell>
//                         <VenueCapsule venue={tripleg.destination}/>
//                         <Box>{t("driver.directAdmit")}</Box>
//                     </TimelineCell>,
//                     color: color,
//                     variant: variant,
//                 });
//             }
//         } else {
//             rv.push({
//                 when: {at: tripleg.arrived},
//                 what: <TimelineCell>
//                     <VenueCapsule venue={tripleg.destination}/>
//                     <Box>{t("driver.arrived")}</Box>
//                 </TimelineCell>,
//                 color: color,
//                 variant: variant,
//             });
//         }
//     }
//
//     return rv;
// }
//
// function useTruckTriplegTimelineSceneProps(args: TruckTimeslice): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const {t} = useTranslation();
//     const mama = useMothership();
//     const {data: triplegStubData, isSuccess: isTriplegStubsSuccess}
//         = useRefreshableQuery(triplegsForTruckDuringTqo(mama, {truckId: args.truckId, during: args.span.toString()}));
//     const triplegStubs = triplegStubData?.triplegs ?? NADA;
//     const {data: triplegs, isSuccess: isTriplegsSuccess} = useQueries({
//         queries: triplegStubs.map(stub => watchTriplegByIdTqo(mama, {triplegId: stub.id})),
//         combine: useMappingMergeUseQueryResultCombiner(
//             rpc => rpc.tripleg),
//     });
//
//     const rv = useMemo(() => {
//         return isTriplegStubsSuccess && isTriplegsSuccess
//             ? {
//                 data: triplegs.reduce<TimelineSceneViewProps[]>((acc, tripleg) => {
//                     return [...acc, ...makeTriplegTimelineSceneProps({
//                         t, tripleg, cutspan: args.span
//                     })];
//                 }, []), isSuccess: true
//             } : MADA;
//     }, [args.span, isTriplegStubsSuccess, isTriplegsSuccess, t, triplegs]);
//
//     return rv;
// }


// function makeTransferTimelineSceneProps(
//     {t, transfer, bundle, cutspan}: {
//         t: TFunction,
//         transfer: BundleTransfer,
//         bundle: Bundle,
//         cutspan?: TstzSpan
//     }): TimelineSceneViewProps[] {
//     const rv: TimelineSceneViewProps[] = [];
//     const color = "neutral";
//     const variant = "soft";
//
//     if (!cutspan || cutspan.contains(transfer.onloadStarted) || cutspan.contains(transfer.onloadEnded)) {
//         if (isTstzFinite(transfer.onloadEnded)) {
//             const onloadSpan = new TstzSpan({
//                 lower: transfer.onloadStarted, upper: transfer.onloadEnded
//             });
//             if (!isValidTstzSpan(onloadSpan))
//                 throw new Error(`invalid span ${onloadSpan.toString()}`);
//             rv.push({
//                 when: {span: onloadSpan},
//                 what: <TimelineCell>
//                     <RouterJoyLink to="/bundle/$id/cargo" params={{id: bundle.id}} color={color}>
//                         <BundleCapsule bundle={bundle}/>
//                     </RouterJoyLink>
//                     <Box>{t("driver.onloaded")}</Box>
//                 </TimelineCell>,
//                 color, variant,
//             });
//         } else {
//             rv.push({
//                 when: {at: transfer.onloadStarted!},
//                 what: <TimelineCell>
//                     <RouterJoyLink to="/bundle/$id/cargo" params={{id: bundle.id}} color={color}>
//                         <BundleCapsule bundle={bundle}/>
//                     </RouterJoyLink>
//                     <Box>{t("driver.startOnloading")}</Box>
//                 </TimelineCell>,
//                 color, variant,
//             });
//         }
//     }
//
//     if (!cutspan || cutspan.contains(transfer.offloadStarted) || cutspan.contains(transfer.offloadEnded)) {
//         if (isTstzFinite(transfer.offloadEnded)) {
//             const offloadSpan = new TstzSpan({
//                 lower: transfer.offloadStarted, upper: transfer.offloadEnded
//             });
//             if (!isValidTstzSpan(offloadSpan))
//                 throw new Error(`invalid span ${offloadSpan.toString()}`);
//             rv.push({
//                 when: {span: offloadSpan},
//                 what: <TimelineCell>
//                     <RouterJoyLink to="/bundle/$id/cargo" params={{id: bundle.id}} color={color}>
//                         <BundleCapsule bundle={bundle}/>
//                     </RouterJoyLink>
//                     <Box>{t("driver.offloaded")}</Box>
//                 </TimelineCell>,
//                 color: "neutral",
//             });
//         } else {
//             rv.push({
//                 when: {at: transfer.offloadStarted!},
//                 what: <TimelineCell>
//                     <RouterJoyLink to="/bundle/$id/cargo" params={{id: bundle.id}} color={color}>
//                         <BundleCapsule bundle={bundle}/>
//                     </RouterJoyLink>
//                     <Box>{t("driver.offloading")}</Box>
//                 </TimelineCell>
//             });
//         }
//     }
//
//     return rv;
// }
//
// function useTruckTransferTimelineSceneProps(args: TruckTimeslice): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const {t} = useTranslation();
//     const mama = useMothership();
//     const {data: transferStubData, isSuccess: isTransferStubsSuccess}
//         = useRefreshableQuery(transfersForTruckDuringTqo(mama, {truckId: args.truckId, during: args.span.toString()}));
//     const transferStubs = transferStubData?.transfers ?? NADA;
//     const {data: bundledTransfers, isSuccess: isTransfersSuccess} = useBundleExpandedTransfers({transferStubs});
//
//     const rv = isTransferStubsSuccess && isTransfersSuccess
//         ? {
//             data: bundledTransfers.reduce<TimelineSceneViewProps[]>((acc, {transfer, bundle}) => {
//                 return [...acc, ...makeTransferTimelineSceneProps({
//                     t, transfer, bundle, cutspan: args.span
//                 })];
//             }, []), isSuccess: true
//         } : MADA;
//
//     return rv;
// }
//
// // these are guaranteed to be sorted
// function useTruckTimelineSceneProps({sortOrder, ...slice}: TruckTimeslice & {
//     sortOrder: SortOrder
// }): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const {data: maybeStampScenes, isSuccess: isStampScenesSuccess}
//         = useTruckStampTimelineScenes(slice);
//     const {data: maybeTruckCrewingScenes, isSuccess: isTruckCrewingScenesSuccess}
//         = useTruckCrewTenureTimelineScenes(slice);
//     const {data: maybeTriplegScenes, isSuccess: isTriplegScenesSuccess}
//         = useTruckTriplegTimelineSceneProps(slice);
//     const {data: maybeTransferScenes, isSuccess: isTransferScenesSuccess}
//         = useTruckTransferTimelineSceneProps(slice);
//
//     const unsorted = useMemo(() => {
//         const stampScenes = maybeStampScenes ?? NADA;
//         const truckCrewingScenes = maybeTruckCrewingScenes ?? NADA;
//         const triplegScenes = maybeTriplegScenes ?? NADA;
//         const transferScenes = maybeTransferScenes ?? NADA;
//         return [
//             ...stampScenes,
//             ...truckCrewingScenes,
//             ...triplegScenes,
//             ...transferScenes,
//         ];
//     }, [maybeStampScenes, maybeTransferScenes, maybeTriplegScenes, maybeTruckCrewingScenes]);
//     const cmp = useTimeCoordinatedComparator(sortOrder);
//     const rv: TimelineSceneViewProps[] = useMemo(() => {
//         const sorted = unsorted.toSorted(cmp);
//         return sorted;
//     }, [cmp, unsorted]);
//
//     if (!isStampScenesSuccess || !isTruckCrewingScenesSuccess || !isTriplegScenesSuccess
//         || !isTransferScenesSuccess) {
//         return MADA;
//     }
//
//     return {data: rv, isSuccess: true};
// }

export interface TruckTimelineViewProps {
    truckId: string;
    // day?: Date;
    // onDayChanged: (newDay?: Date) => void;
    sortOrder: SortOrder;
}

// export function TruckTimelineView(
//     {
//         truckId,
//         // day: inDay,
//         // onDayChanged,
//         sortOrder,
//     }: TruckTimelineViewProps) {
//     const [day, setDay] = useState<Date>(new Date());
//     const span = MakeDayTstzSpan(day);
//     const {data: sceneProps, isSuccess: isScenePropsSuccess}
//         = useTruckTimelineSceneProps({truckId, span, sortOrder});
//     const {mutateAsync: performStamp} = useStampTruckMutation();
//     const spawnGenericStamp = useCallback((gfsf: GenericFlavorlessStampSpawnFood) =>
//         performStamp({food: {truckId, ...gfsf}}), [performStamp, truckId]);
//
//     if (!isScenePropsSuccess) {
//         return null;
//     }
//
//     return <Stack direction="column" sx={{alignItems: "center", mx: 2}}>
//         <DateBrowser day={day} onDayChange={(nv) => {
//             setDay(nv ?? new Date());
//         }} sx={{position: "sticky", top: 12, mb: 0.4, zIndex: 140}}/>
//         <TimelineView sortedSceneProps={sceneProps}
//                       onStampInput={spawnGenericStamp}/>
//     </Stack>
// }


export const truckTimelineVerb = "TruckTimeline";

export interface WatchTruckTimelineArgs {
    truckId: string;
}

export function truckTimelineTqk(args?: WatchTruckTimelineArgs) {
    return args ? [truckTimelineVerb, args] as const : [truckTimelineVerb] as const;
}

// this can go stale unless there is a relay mounted watching over it

export function ensureUnsortedTruckTimeline({tqc, mama, args}: {
    tqc: QueryClient,
    mama: KingBasura,
    args: WatchTruckTimelineArgs
}): Promise<TruckScene[]> {
    const qa = {tqc, mama, args} as const;
    return Promise.all([
        ensureTruckById(qa),
        ensureTriplegsForTruck(qa),
        ensureStampsForTruck(qa),
        ensureTransfersForTruck(qa),
        ensureCrewByTruck(qa),
    ]).then(([{truck}, {triplegs: triplegStubs}, {stamps}, {transfers: transferStubs}, crewTenures]) => {
        if (!truck) {
            throw new Error(`no such truck ${args.truckId}`)
        }
        return Promise.all([
            Promise.all(triplegStubs.map(stub =>
                ensureTriplegById({tqc, mama, args: {triplegId: stub.id}})
                    .then(rpc => rpc.tripleg))),
            Promise.all(transferStubs.map(stub =>
                ensureTransferById({tqc, mama, args: {transferId: stub.id}})
                    .then(rpc => rpc.transfer))),
        ]).then(([triplegs, transfers]) => {
            const now = new Date();
            const food: TruckTimelineFood = {
                truck,
                triplegs, triplegsAsof: now,
                stamps, stampsAsof: now,
                transfers, transfersAsof: now,
                crewTenures: crewTenures.tenures, crewTenuresAsof: now,
            };
            return food;
        }).then(food => {
            const scenes = synthTruckScenes(food);
            return scenes;
        });
    });
}

export function truckTimelineTqo({tqc, mama, args}: {
    tqc: QueryClient,
    mama: KingBasura,
    args?: WatchTruckTimelineArgs
}) {
    return queryOptions({
        queryKey: truckTimelineTqk(args),
        queryFn: args ? () => ensureUnsortedTruckTimeline({tqc, mama, args}) : skipToken,
    });
}

export function useTruckTimeline(args?: WatchTruckTimelineArgs): InfallibleUseQueryResult<TruckScene[], Error> {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useRefreshableInfallibleQuery(truckTimelineTqo({tqc, mama, args}));
}

export function TruckDiaryView({truckId, sortOrder}: TruckTimelineViewProps) {
    const truckTqrv = useTruckById({truckId});
    const timelineTqrv = useTruckTimeline({truckId});
    const [day, setDay] = useState<DateArg<Date>>(new Date());

    const rawScenes = timelineTqrv.data
    const sorted = useMemo(() => {
        switch (sortOrder) {
            case "ASC":
                return rawScenes?.toReversed() ?? [];
            case "DESC":
                return rawScenes ?? [];
        }
    }, [rawScenes, sortOrder]);

    if (truckTqrv.isPending || timelineTqrv.isPending) {
        return <SuspenseDialog message="fetching timeline"/>;
    }

    return <Stack direction="column" sx={{alignItems: "center", mx: 2}}>
        <DateBrowser day={day} onDayChange={(nv) => {
            setDay(nv ?? new Date());
        }} sx={{position: "sticky", top: 12, mb: 0.4, zIndex: 140}}/>

        <DiaryView<TruckScene>
            sortedScenes={sorted}
            Entry={TruckSceneView}
            targetLabel={<TruckCapsule truck={truckTqrv.data}/>}
        />
    </Stack>
}

export const lastSceneForTruckVerb = "LastSceneForTruck";

export function lastSceneForTruckTqk(args ?: WatchTruckTimelineArgs) {
    return args ? [lastSceneForTruckVerb, args] as const : [lastSceneForTruckVerb] as const;
}

export function ensureLastSceneForTruck(
    args: {
        tqc: QueryClient, mama: KingBasura, args: WatchTruckTimelineArgs
    }
) {
    return ensureUnsortedTruckTimeline(args)
        .then(unsorted => {
            const sorted = unsorted.toSorted(cmpScenesNewestFirst);
            const last = sorted[0];
            return last ?? null;
        });
}

export function lastSceneForTruckTqo(mama: KingBasura, args?: WatchTruckTimelineArgs) {
    return queryOptions({

        queryKey: lastSceneForTruckTqk(args),
        queryFn: args
            ? ({client: tqc}) => ensureLastSceneForTruck({tqc, mama, args})
            : skipToken,
    });
}

export function useLastSceneForTruck(args?: WatchTruckTimelineArgs) {
    const mama = useMothership();
    return useRefreshableInfallibleQuery(lastSceneForTruckTqo(mama, args));
}

export function TruckTimelineRelay(args: { truckId: string }) {
    const truckId = args.truckId;
    const tqc = useQueryClient();
    const tqk = truckTimelineTqk(args);
    const truckTqrv = useTruckById(args)
    const triplegsTqrv = useFullTriplegsForTruck(args);
    const stampsTqrv = useStampsForTruck(args);
    const transfersTqrv = useFullTransfersForTruck(args);
    const crewTqrv = useCrewByTruck(args);

    let triplegs: Tripleg[] | undefined;
    let triplegsAsof: DateArg<Date> | undefined;
    if (triplegsTqrv.isSuccess) {
        triplegs = cleanse(triplegsTqrv.data);
        triplegsAsof = triplegsTqrv.dataUpdatedAt;
    }

    let transfers: Transfer[] | undefined;
    let transfersAsof: DateArg<Date> | undefined;
    if (transfersTqrv.isSuccess) {
        transfers = cleanse(transfersTqrv.data);
        transfersAsof = transfersTqrv.dataUpdatedAt;
    }

    const synthFoodZrv = useMemo(() => {
        const maybe: Partial<TruckTimelineFood> = {
            truck: truckTqrv.data,
            stamps: stampsTqrv.data,
            stampsAsof: stampsTqrv.dataUpdatedAt,
            crewTenures: crewTqrv.data,
            crewTenuresAsof: crewTqrv.dataUpdatedAt,
            triplegs, triplegsAsof,
            transfers, transfersAsof,
        };
        const rvz = TruckTimelineFood.safeParse(maybe);
        return rvz;
    }, [truckTqrv.data, stampsTqrv.data, stampsTqrv.dataUpdatedAt,
        crewTqrv.data, crewTqrv.dataUpdatedAt, triplegs, triplegsAsof, transfers, transfersAsof]);

    useEffect(() => {
        if (synthFoodZrv.success) {
            const scenes = synthTruckScenes(synthFoodZrv.data);
            tqc.setQueryData(tqk, scenes);
            const latestScene = scenes[0];

            if (latestScene) {
                tqc.setQueryData(lastSceneForTruckTqk({truckId}), latestScene);
            }
        }
    }, [synthFoodZrv.data, synthFoodZrv.success, tqc, tqk, truckId]);

    return null;
}

function cmpTruckActorsStableOrder(a: TruckActor, b: TruckActor) {
    return cmpById(a.truck, b.truck);
}

function cmpTruckActorsActiveOrder(a: TruckActor, b: TruckActor) {
    return cmpActorScenesNewestFirst(a, b);
}

export const dispatchTruckAscSorters: Record<TieredOrActive, Comparator<TruckActor>> = {
    tiered: cmpTruckActorsStableOrder,
    active: cmpTruckActorsActiveOrder,
} as const;

export function useTruckSceneColor(scene ?: TruckScene | null) {
    const fromTripleg = useTriplegSceneColor(scene?.sceneFlavor === "tripleg"
        ? scene.triplegScene : undefined);
    return fromTripleg;
}



